/**
 * Login Page - 로그인/회원가입
 * 테마 색은 <html data-role> 의 --primary (마지막 로그인 역할: 사용자 청록 / 관리자 보라, authApi.applyRoleTheme)
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../components/ui';
import { authApi } from '../api';
import apiClient from '../api/client';
import gmailLogo from '../assets/icons/login/gmail.svg';
import naverLogo from '../assets/icons/login/naver.svg';
import notionLogo from '../assets/icons/login/notion.svg';
import './LoginPage.css';

/** 배경에 떠 있는 연동 서비스 타일 (1200px 이하에서는 숨김) */
function FloatingTiles() {
  return (
    <div className="login-tiles" aria-hidden="true">
      {/* Slack */}
      <div className="lt xl" style={{ left: '19%', top: '17%', animationDelay: '-1s' }}>
        <svg viewBox="0 0 122.8 122.8">
          <path fill="#E01E5A" stroke="none" d="M25.8 77.6c0 7.1-5.8 12.9-12.9 12.9S0 84.7 0 77.6s5.8-12.9 12.9-12.9h12.9v12.9zM32.3 77.6c0-7.1 5.8-12.9 12.9-12.9s12.9 5.8 12.9 12.9v32.3c0 7.1-5.8 12.9-12.9 12.9s-12.9-5.8-12.9-12.9V77.6z" />
          <path fill="#36C5F0" stroke="none" d="M45.2 25.8c-7.1 0-12.9-5.8-12.9-12.9S38.1 0 45.2 0s12.9 5.8 12.9 12.9v12.9H45.2zM45.2 32.3c7.1 0 12.9 5.8 12.9 12.9s-5.8 12.9-12.9 12.9H12.9C5.8 58.1 0 52.3 0 45.2s5.8-12.9 12.9-12.9h32.3z" />
          <path fill="#2EB67D" stroke="none" d="M97 45.2c0-7.1 5.8-12.9 12.9-12.9s12.9 5.8 12.9 12.9-5.8 12.9-12.9 12.9H97V45.2zM90.5 45.2c0 7.1-5.8 12.9-12.9 12.9s-12.9-5.8-12.9-12.9V12.9C64.7 5.8 70.5 0 77.6 0s12.9 5.8 12.9 12.9v32.3z" />
          <path fill="#ECB22E" stroke="none" d="M77.6 97c7.1 0 12.9 5.8 12.9 12.9s-5.8 12.9-12.9 12.9-12.9-5.8-12.9-12.9V97h12.9zM77.6 90.5c-7.1 0-12.9-5.8-12.9-12.9s5.8-12.9 12.9-12.9h32.3c7.1 0 12.9 5.8 12.9 12.9s-5.8 12.9-12.9 12.9H77.6z" />
        </svg>
      </div>
      {/* 네이버 - 심볼이 꽉 차 보여서 타일만 4px 키움 (로고 크기는 l 그대로) */}
      <div className="lt l naver" style={{ right: '19%', top: '19%', animationDelay: '-4s' }}>
        <img src={naverLogo} alt="" />
      </div>
      {/* Google Drive */}
      <div className="lt l gdrive" style={{ left: '24%', top: '31%', animationDelay: '-3s' }}>
        <svg viewBox="0 0 24 24">
          <polygon points="8,3 11.6,9.2 5,21 1.4,15" fill="#0F9D58" stroke="none" />
          <polygon points="8,3 16,3 22.6,15 15.4,15" fill="#FFBA00" stroke="none" />
          <polygon points="5,21 19,21 22.6,15 8.6,15" fill="#2684FC" stroke="none" />
        </svg>
      </div>
      <div className="lt m b1" style={{ left: '20%', top: '58%', animationDelay: '-8s' }}>
        <svg viewBox="0 0 24 24"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
      </div>
      {/* AI 챗봇 */}
      <div className="lt l" style={{ left: '25%', top: '71%', animationDelay: '-5s' }}>
        <svg viewBox="0 0 24 24">
          <rect x="4" y="7" width="16" height="13" rx="4" />
          <path d="M12 4v3M2 12v4M22 12v4M9.5 15.5c1.4 1 3.6 1 5 0" />
          <circle cx="12" cy="3.5" r="1" />
          <circle cx="9" cy="12" r="1" fill="currentColor" />
          <circle cx="15" cy="12" r="1" fill="currentColor" />
        </svg>
      </div>
      <div className="lt xs b3" style={{ left: '11%', top: '86%', animationDelay: '-6s' }}>
        <svg viewBox="0 0 24 24"><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></svg>
      </div>
      <div className="lt xs b2" style={{ left: '31%', top: '11%', animationDelay: '-2s' }}>
        <svg viewBox="0 0 24 24"><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18M8 3v4M16 3v4" /></svg>
      </div>
      <div className="lt s" style={{ right: '32%', top: '84%', animationDelay: '-2s' }}>
        <img src={notionLogo} alt="" />
      </div>
      <div className="lt xl" style={{ right: '20%', top: '66%', animationDelay: '-5s' }}>
        <img src={gmailLogo} alt="" />
      </div>
      <div className="lt xs b2" style={{ right: '24%', top: '36%', animationDelay: '-7s' }}>
        <svg viewBox="0 0 24 24"><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" /><path d="M14 3v5h5M9 13h6M9 17h4" /></svg>
      </div>
      <div className="lt l b3" style={{ right: '13%', top: '50%', animationDelay: '-3s' }}>
        <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" /></svg>
      </div>
      <div className="lt s b2" style={{ left: '13%', top: '42%', animationDelay: '-7s' }}>
        <svg viewBox="0 0 24 24"><path d="M22 2 11 13M22 2l-7 20-4-9-9-4z" /></svg>
      </div>
      <div className="lt s b2" style={{ right: '30%', top: '9%', animationDelay: '-1s' }}>
        <svg viewBox="0 0 24 24"><path d="M4 6h16M4 10h16M4 14h10M4 18h7" /></svg>
      </div>
    </div>
  );
}

export function LoginPage() {
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [isRegister, setIsRegister] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    email: '',
    password: '',
    name: '',
  });

  // 비밀번호 재설정 state
  const [resetStep, setResetStep] = useState<0 | 1 | 2 | 3>(0); // 0=로그인, 1=이름+이메일, 2=인증코드, 3=새비밀번호
  const [resetForm, setResetForm] = useState({ name: '', email: '', code: '', newPassword: '', newPasswordConfirm: '' });
  const [resetToken, setResetToken] = useState('');

  const handleRequestReset = async () => {
    if (!resetForm.email.trim()) { showToast('error', '이메일을 입력해주세요.'); return; }
    try {
      setLoading(true);
      await apiClient.post('/api/auth/request-reset', { email: resetForm.email.trim() });
      showToast('success', '인증번호가 이메일로 발송되었습니다.');
      setResetStep(2);
    } catch (e) {
      showToast('error', (e as any)?.response?.data?.error?.message || '오류가 발생했습니다.');
    } finally { setLoading(false); }
  };

  const handleVerifyCode = async () => {
    if (!resetForm.code.trim()) { showToast('error', '인증번호를 입력해주세요.'); return; }
    try {
      setLoading(true);
      const res = await apiClient.post<{ success: boolean; data: { resetToken: string } }>('/api/auth/verify-reset-code', { email: resetForm.email.trim(), code: resetForm.code.trim() });
      setResetToken(res.data.data.resetToken);
      setResetStep(3);
    } catch (e) {
      showToast('error', (e as any)?.response?.data?.error?.message || '인증번호가 올바르지 않습니다.');
    } finally { setLoading(false); }
  };

  const handleResetPassword = async () => {
    if (!resetForm.newPassword || resetForm.newPassword.length < 6) { showToast('error', '비밀번호는 6자 이상이어야 합니다.'); return; }
    if (resetForm.newPassword !== resetForm.newPasswordConfirm) { showToast('error', '비밀번호가 일치하지 않습니다.'); return; }
    try {
      setLoading(true);
      await apiClient.post('/api/auth/reset-password', { resetToken, newPassword: resetForm.newPassword });
      showToast('success', '비밀번호가 변경되었습니다. 로그인해주세요.');
      setResetStep(0);
      setResetForm({ name: '', email: '', code: '', newPassword: '', newPasswordConfirm: '' });
      setResetToken('');
    } catch (e) {
      showToast('error', (e as any)?.response?.data?.error?.message || '오류가 발생했습니다.');
    } finally { setLoading(false); }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!form.email || !form.password) {
      showToast('error', '이메일과 비밀번호를 입력해주세요.');
      return;
    }

    if (isRegister && !form.name) {
      showToast('error', '이름을 입력해주세요.');
      return;
    }

    try {
      setLoading(true);

      if (isRegister) {
        await authApi.register({
          email: form.email,
          password: form.password,
          name: form.name,
        });
        showToast('success', '회원가입이 완료되었습니다. 로그인해주세요.');
        setIsRegister(false);
        setForm({ ...form, password: '' });
      } else {
        await authApi.login({
          email: form.email,
          password: form.password,
        });
        showToast('success', '로그인되었습니다.');

        // 로그인 후 플랫폼 홈으로 이동 (/ 는 공개 사이트 홈)
        navigate('/home');
      }
    } catch (error) {
      const message = (error as { response?: { data?: { error?: { message?: string } } } })
        ?.response?.data?.error?.message || '오류가 발생했습니다.';
      showToast('error', message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-stage">
      <FloatingTiles />

      <div className="login-card">
        <div className="login-brand">
          {/* 서비스 로고 - 현재 테마 역할(<html data-role>, 비로그인 = 마지막 로그인 역할) 색 */}
          <h1 className="login-logo">
            <img
              src={document.documentElement.dataset.role === 'admin' ? '/logo-acxionon-admin.svg' : '/logo-acxionon-user.svg'}
              alt="Acxion-On"
            />
          </h1>
          <p>{resetStep > 0 ? '비밀번호 변경' : isRegister ? '새 계정을 만드세요' : '로그인하여 시작하세요'}</p>
        </div>

        {/* 비밀번호 재설정 Step 1: 이메일 */}
        {resetStep === 1 && (
          <div className="login-form">
            <div className="login-field">
              <label>이메일</label>
              <input type="email" value={resetForm.email} onChange={e => setResetForm({...resetForm, email: e.target.value})} placeholder="이메일 주소" autoComplete="email" />
            </div>
            <button type="button" className="login-submit" onClick={handleRequestReset} disabled={loading}>{loading ? '발송 중...' : '인증번호 요청'}</button>
            <button type="button" className="login-back" onClick={() => { setResetStep(0); setResetForm({ name:'', email:'', code:'', newPassword:'', newPasswordConfirm:'' }); }}>뒤로가기</button>
          </div>
        )}

        {/* 비밀번호 재설정 Step 2: 인증코드 입력 */}
        {resetStep === 2 && (
          <div className="login-form">
            <div className="login-field">
              <label>이메일</label>
              <input type="email" value={resetForm.email} readOnly />
            </div>
            <button type="button" className="login-resend" onClick={handleRequestReset} disabled={loading}>{loading ? '발송 중...' : '인증번호 재요청'}</button>
            <div className="login-field">
              <label>인증번호</label>
              <input type="text" value={resetForm.code} onChange={e => setResetForm({...resetForm, code: e.target.value})} placeholder="6자리 인증번호" maxLength={6} inputMode="numeric" />
            </div>
            <button type="button" className="login-submit" onClick={handleVerifyCode} disabled={loading}>{loading ? '확인 중...' : '인증'}</button>
            <button type="button" className="login-back" onClick={() => setResetStep(1)}>뒤로가기</button>
          </div>
        )}

        {/* 비밀번호 재설정 Step 3: 새 비밀번호 */}
        {resetStep === 3 && (
          <div className="login-form">
            <div className="login-field">
              <label>이메일</label>
              <input type="email" value={resetForm.email} readOnly />
            </div>
            <div className="login-field">
              <label>신규 비밀번호</label>
              <input type="password" value={resetForm.newPassword} onChange={e => setResetForm({...resetForm, newPassword: e.target.value})} placeholder="신규 비밀번호 (6자 이상)" autoComplete="new-password" />
            </div>
            <div className="login-field">
              <label>신규 비밀번호 확인</label>
              <input type="password" value={resetForm.newPasswordConfirm} onChange={e => setResetForm({...resetForm, newPasswordConfirm: e.target.value})} placeholder="신규 비밀번호 확인" autoComplete="new-password" />
            </div>
            <div className="login-reset-actions">
              <button type="button" className="login-back" onClick={() => setResetStep(0)}>뒤로가기</button>
              <button type="button" className="login-submit login-submit--reset" onClick={handleResetPassword} disabled={loading}>{loading ? '변경 중...' : '비밀번호 재설정'}</button>
            </div>
          </div>
        )}

        {/* 로그인 폼 */}
        {resetStep === 0 && (
        <form className="login-form" onSubmit={handleSubmit}>
          {isRegister && (
            <div className="login-field">
              <label htmlFor="login-name">이름</label>
              <input
                id="login-name"
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="홍길동"
                autoComplete="name"
              />
            </div>
          )}

          <div className="login-field">
            <label htmlFor="login-email">이메일</label>
            <input
              id="login-email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="이메일 주소"
              autoComplete="email"
            />
          </div>

          <div className="login-field">
            <label htmlFor="login-password">비밀번호</label>
            <input
              id="login-password"
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder="●●●●●●"
              autoComplete={isRegister ? 'new-password' : 'current-password'}
            />
          </div>

          <button type="submit" className="login-submit" disabled={loading}>
            {loading ? '처리 중...' : isRegister ? '회원가입' : '로그인'}
          </button>
        </form>
        )}

        {/* 비밀번호 변경 링크 - 로그인 화면에서만 표시 */}
        {resetStep === 0 && (
        <p className="login-alt">
          <button type="button" className="login-forgot" onClick={() => setResetStep(1)}>비밀번호 변경</button>
        </p>
        )}
      </div>

      {/* 하단 회사 로고 - 회사 고유 색상 그대로 (public/logo-etech.svg) */}
      <div className="login-foot">
        <img className="login-etech" src="/logo-etech.svg" alt="ETECH SYSTEM" />
      </div>
    </div>
  );
}

export default LoginPage;
